import { addDays, format } from 'date-fns';
import { QuestionAnalysisCountsDailyStat } from './dashboardApi';
import { createDateBuckets } from './messagesStats';

export interface RelatedQuestionsDataset {
  labels: string[];
  values: number[];
}

const DATE_FORMAT = 'yyyy-MM-dd';

export const buildRelatedQuestionsDataset = (
  dailyStats: QuestionAnalysisCountsDailyStat[],
  startDate: string,
  endDate: string
): RelatedQuestionsDataset => {
  const start = parseDate(startDate);
  const end = parseDate(endDate);

  if (!start || !end || start > end) {
    return { labels: [], values: [] };
  }

  const normalizedStats = normalizeDailyStats(dailyStats);
  const buckets = createDateBuckets(start, end);

  const labels = buckets.map((bucket) => bucket.label);
  const values = buckets.map((bucket) =>
    sumStatsInRange(normalizedStats, bucket.start, bucket.end)
  );

  return { labels, values };
};

const parseDate = (value: string): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeDailyStats = (dailyStats: QuestionAnalysisCountsDailyStat[]) => {
  const statsMap = new Map<string, number>();
  dailyStats.forEach((stat) => {
    if (!stat?.date) return;
    const parsed = parseDate(stat.date);
    if (!parsed) return;
    const key = format(parsed, DATE_FORMAT);
    const relatedCount = Math.max(
      0,
      (stat?.total_messages ?? 0) - (stat?.non_related_messages ?? 0)
    );
    statsMap.set(key, relatedCount);
  });
  return statsMap;
};

const sumStatsInRange = (
  statsMap: Map<string, number>,
  start: Date,
  end: Date
): number => {
  let current = start;
  let total = 0;

  while (current <= end) {
    const key = format(current, DATE_FORMAT);
    total += statsMap.get(key) ?? 0;
    current = addDays(current, 1);
  }

  return total;
};
